/**
 * @file AppearanceSettingsPage.js
 * @description Configuración → Apariencia: elegir paleta y modo claro/oscuro.
 *
 * ⚠ TEMPORAL. Mientras se decide cuál de las tres paletas se queda. El selector
 * rápido de la navbar sirve para comparar sobre la marcha; esta página es para
 * mirar cada propuesta con calma y entender qué se está eligiendo.
 *
 * La preferencia se guarda solo en este navegador (localStorage). No viaja al
 * backend ni toca la base de datos de ningún negocio.
 */
import React from 'react';
import { Card, Row, Col, Alert, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../theme/ThemeContext';
import { PALETTES, MODES } from '../theme/palettes';
import { PaletteSwatches } from '../components/ThemeSwitcher';

/**
 * Vista previa en miniatura de una paleta: una barra, un par de botones, una
 * fila de tabla y los estados. Se pinta con `data-ev-palette` propio, así que
 * muestra SU paleta aunque la activa sea otra — que es todo el punto.
 */
const PalettePreview = ({ paletteId }) => (
    <div
        data-ev-palette={paletteId}
        aria-hidden="true"
        style={{
            background: 'var(--ev-bg-canvas)',
            border: '1px solid var(--ev-border-default)',
            borderRadius: 'var(--ev-radius-md)',
            padding: '0.6rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.45rem',
        }}
    >
        <div style={{
            background: 'var(--ev-bg-inverse)',
            color: 'var(--ev-text-on-inverse)',
            borderRadius: 'var(--ev-radius-sm)',
            padding: '0.3rem 0.5rem',
            fontSize: '0.7rem',
            fontWeight: 700,
        }}>
            Evazard
        </div>

        <div style={{ display: 'flex', gap: '0.3rem' }}>
            <span style={{
                background: 'var(--ev-action-primary-bg)', color: 'var(--ev-action-primary-fg)',
                borderRadius: 'var(--ev-radius-sm)', padding: '0.2rem 0.5rem',
                fontSize: '0.68rem', fontWeight: 600,
            }}>Guardar</span>
            <span style={{
                background: 'var(--ev-action-secondary-bg)', color: 'var(--ev-action-secondary-fg)',
                border: '1px solid var(--ev-action-secondary-border)',
                borderRadius: 'var(--ev-radius-sm)', padding: '0.2rem 0.5rem',
                fontSize: '0.68rem', fontWeight: 600,
            }}>Cancelar</span>
        </div>

        <div style={{
            background: 'var(--ev-bg-surface)',
            border: '1px solid var(--ev-border-default)',
            borderRadius: 'var(--ev-radius-sm)',
            padding: '0.35rem 0.5rem',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontSize: '0.68rem',
        }}>
            <span style={{ color: 'var(--ev-text-brand)', fontWeight: 700 }}>FV-1042</span>
            <span style={{ color: 'var(--ev-text-primary)', fontVariantNumeric: 'tabular-nums' }}>$124.900</span>
        </div>

        <div style={{ display: 'flex', gap: '0.25rem' }}>
            {[
                ['--ev-success-bg', '--ev-success-fg', '--ev-success-border'],
                ['--ev-warning-bg', '--ev-warning-fg', '--ev-warning-border'],
                ['--ev-danger-bg', '--ev-danger-fg', '--ev-danger-border'],
            ].map(([bg, fg, border]) => (
                <span key={bg} style={{
                    background: `var(${bg})`, color: `var(${fg})`,
                    border: `1px solid var(${border})`,
                    borderRadius: 'var(--ev-radius-sm)',
                    padding: '0.1rem 0.4rem', fontSize: '0.62rem', fontWeight: 700, flex: 1,
                    textAlign: 'center',
                }}>●</span>
            ))}
        </div>
    </div>
);

const AppearanceSettingsPage = () => {
    const { t } = useTranslation();
    const { palette, mode, setPalette, setMode } = useTheme();

    return (
        <Card>
            <Card.Header as="h2" className="h4 mb-0">{t('appearance.title')}</Card.Header>
            <Card.Body>
                <Alert variant="info" className="d-flex gap-2 align-items-start">
                    <i className="bi bi-info-circle-fill" aria-hidden="true" />
                    <div>
                        <strong className="d-block">{t('appearance.noticeTitle')}</strong>
                        {t('appearance.noticeBody')}
                    </div>
                </Alert>

                <h3 className="h6 text-uppercase text-muted mt-4 mb-3">{t('appearance.paletteLabel')}</h3>
                <Row className="g-3">
                    {PALETTES.map((p) => {
                        const selected = palette === p.id;
                        return (
                            <Col md={4} key={p.id}>
                                <Card
                                    className={`h-100 ${selected ? 'border-primary border-2' : ''}`}
                                    data-testid={`appearance-palette-${p.id}`}
                                >
                                    <Card.Body className="d-flex flex-column gap-2">
                                        <div className="d-flex justify-content-between align-items-start gap-2">
                                            <div>
                                                <div className="fw-bold">{t(p.labelKey)}</div>
                                                <div className="small text-muted">{t(p.taglineKey)}</div>
                                            </div>
                                            {selected && (
                                                <span className="badge text-bg-primary">
                                                    {t('appearance.inUse')}
                                                </span>
                                            )}
                                        </div>

                                        <PaletteSwatches paletteId={p.id} swatches={p.swatches} size={18} />
                                        <PalettePreview paletteId={p.id} />

                                        <Button
                                            variant={selected ? 'primary' : 'outline-primary'}
                                            size="sm"
                                            className="mt-auto"
                                            onClick={() => setPalette(p.id)}
                                            disabled={selected}
                                        >
                                            {selected ? t('appearance.inUse') : t('appearance.use')}
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>

                <h3 className="h6 text-uppercase text-muted mt-4 mb-3">{t('appearance.modeLabel')}</h3>
                <div className="d-flex flex-wrap gap-2">
                    {MODES.map((m) => (
                        <Button
                            key={m.id}
                            variant={mode === m.id ? 'primary' : 'outline-secondary'}
                            onClick={() => setMode(m.id)}
                            data-testid={`appearance-mode-${m.id}`}
                        >
                            <i className={`bi ${m.icon} me-2`} aria-hidden="true" />
                            {t(m.labelKey)}
                        </Button>
                    ))}
                </div>
                <p className="small text-muted mt-2 mb-0">{t('appearance.modeHelp')}</p>

                <hr className="my-4" />
                <p className="small text-muted mb-0">{t('appearance.feedback')}</p>
            </Card.Body>
        </Card>
    );
};

export default AppearanceSettingsPage;
